import { parseGIF, decompressFrames } from 'gifuct-js';

export const AnimState = Object.freeze({
  IDLE: 'idle',
  ENTRANCE: 'entrance',
  RESUME: 'resume',
  TRANSITION: 'transition',
  LISTEN_BEGIN: 'listen_begin',
  LISTENING: 'listening',
  LISTEN_END: 'listen_end',
  SPEAK_BEGIN: 'speak_begin',
  SPEAKING: 'speaking',
  SPEAK_END: 'speak_end',
  THINKING: 'thinking',
  ERROR: 'error',
  HOP: 'hop',
  BOW: 'bow',
  STATIC: 'static',
});

const FILES = {
  [AnimState.ENTRANCE]: 'circle_entrance.gif',
  [AnimState.RESUME]: 'cortana_resume.gif',
  [AnimState.TRANSITION]: 'circle_transition_idle.gif',
  [AnimState.LISTEN_BEGIN]: 'circle_begin_listen.gif',
  [AnimState.LISTENING]: 'circle_listening.gif',
  [AnimState.LISTEN_END]: 'circle_listen_end.gif',
  [AnimState.SPEAK_BEGIN]: 'circle_begin_speaking.gif',
  [AnimState.SPEAKING]: 'circle_speaking.gif',
  [AnimState.SPEAK_END]: 'circle_speaking_end.gif',
  [AnimState.THINKING]: 'circle_spin.gif',
  [AnimState.ERROR]: 'circle_error.gif',
  [AnimState.HOP]: 'circle_hop.gif',
  [AnimState.BOW]: 'circle_bow.gif',
  [AnimState.STATIC]: 'circle_static.gif',
  idle_start: 'circle_idle_start.gif',
  idle_mid: 'circle_idle_mid.gif',
  idle_end: 'circle_idle_end.gif',
};

const AUTO = {
  [AnimState.ENTRANCE]: AnimState.TRANSITION,
  [AnimState.RESUME]: AnimState.TRANSITION,
  [AnimState.TRANSITION]: AnimState.IDLE,
  [AnimState.LISTEN_BEGIN]: AnimState.LISTENING,
  [AnimState.LISTEN_END]: AnimState.TRANSITION,
  [AnimState.SPEAK_BEGIN]: AnimState.SPEAKING,
  [AnimState.SPEAK_END]: AnimState.TRANSITION,
  [AnimState.ERROR]: AnimState.TRANSITION,
  [AnimState.HOP]: AnimState.TRANSITION,
  [AnimState.BOW]: AnimState.TRANSITION,
};

const LOOPING = new Set([AnimState.LISTENING, AnimState.SPEAKING, AnimState.THINKING, AnimState.STATIC]);
const cache = new Map();

function luminance(r, g, b) {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function parseHex(hex) {
  const value = (hex || '#0078D7').replace('#', '');
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

class GifRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { willReadFrequently: true });
    this.frames = [];
    this.currentIndex = 0;
    this.timer = 0;
    this.running = false;
    this.loops = 0;
    this.maxLoops = 1;
    this.onComplete = null;
    this.theme = parseHex('#0078D7');
    this._image = null;
  }

  async load(url) {
    this.stop();
    const file = url.split('/').pop();
    let decoded = cache.get(file);
    if (!decoded) {
      const buffer = await fetch(url).then((res) => {
        if (!res.ok) throw new Error('Missing animation ' + file);
        return res.arrayBuffer();
      });
      decoded = decodeGif(buffer);
      cache.set(file, decoded);
    }
    this.frames = decoded.frames.map((frame) => ({ data: frame.data, delay: frame.delay }));
    this.canvas.width = decoded.width;
    this.canvas.height = decoded.height;
    this._image = this.ctx.createImageData(decoded.width, decoded.height);
    this.currentIndex = 0;
    this.loops = 0;
  }

  setTheme(hex) {
    this.theme = parseHex(hex);
    if (this.frames.length) this.render(this.currentIndex % this.frames.length);
  }

  start(loop) {
    const done = this.onComplete;
    this.stop();
    this.onComplete = done;
    this.running = true;
    this.currentIndex = 0;
    this.loops = 0;
    this.maxLoops = loop ? Infinity : 1;
    this.tick();
  }

  once(onComplete) {
    this.onComplete = onComplete;
    this.start(false);
  }

  stop() {
    this.running = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = 0;
    this.onComplete = null;
  }

  tick() {
    if (!this.running || !this.frames.length) return;
    this.render(this.currentIndex);
    this.currentIndex += 1;
    if (this.currentIndex >= this.frames.length) {
      this.loops += 1;
      if (this.loops >= this.maxLoops) {
        this.running = false;
        const done = this.onComplete;
        this.onComplete = null;
        if (done) done();
        return;
      }
      this.currentIndex = 0;
    }
    const delay = this.frames[this.currentIndex].delay;
    this.timer = setTimeout(() => this.tick(), delay);
  }

  render(index) {
    const frame = this.frames[index];
    if (!frame || !this._image) return;
    const out = this._image.data;
    const src = frame.data;
    const { r, g, b } = this.theme;
    out.fill(0);
    for (let i = 0; i < src.length; i += 4) {
      const alpha = src[i + 3];
      if (!alpha) continue;
      const intensity = luminance(src[i], src[i + 1], src[i + 2]) / 255;
      out[i] = Math.round(r * intensity);
      out[i + 1] = Math.round(g * intensity);
      out[i + 2] = Math.round(b * intensity);
      out[i + 3] = alpha;
    }
    this.ctx.putImageData(this._image, 0, 0);
  }
}

function decodeGif(buffer) {
  const gif = parseGIF(buffer);
  const rawFrames = decompressFrames(gif, true);
  const width = gif.lsd.width;
  const height = gif.lsd.height;
  const frames = [];
  let previous = new Uint8ClampedArray(width * height * 4);
  let disposal = 0;

  rawFrames.forEach((raw) => {
    const patch = raw.patch;
    const { left, top, width: fw, height: fh } = raw.dims;
    const frame = new Uint8ClampedArray(width * height * 4);
    if (disposal === 0 || disposal === 1) frame.set(previous);

    for (let y = 0; y < fh; y++) {
      for (let x = 0; x < fw; x++) {
        const si = (y * fw + x) * 4;
        const di = ((top + y) * width + (left + x)) * 4;
        const pr = patch[si];
        const pg = patch[si + 1];
        const pb = patch[si + 2];
        const pa = patch[si + 3];
        if (!pa || luminance(pr, pg, pb) < 10) {
          frame[di + 3] = 0;
          continue;
        }
        frame[di] = pr;
        frame[di + 1] = pg;
        frame[di + 2] = pb;
        frame[di + 3] = 255;
      }
    }

    frames.push({ data: frame, delay: Math.max(raw.delay || 10, 20) });
    previous = raw.disposalType === 2 ? new Uint8ClampedArray(width * height * 4) : new Uint8ClampedArray(frame);
    disposal = raw.disposalType;
  });

  return { width, height, frames };
}

export class CortanaAnimation {
  constructor(canvas) {
    this.renderer = new GifRenderer(canvas);
    this.state = null;
    this.generation = 0;
    this.idle = false;
    this.idleIndex = 1;
  }

  setAccent(hex) {
    this.renderer.setTheme(hex);
  }

  async play(state) {
    const generation = ++this.generation;
    this.idle = false;
    this.state = state;
    if (state === AnimState.IDLE) {
      this.idle = true;
      this.idleIndex = 1;
      await this.playIdle(generation);
      return;
    }
    await this.renderer.load('/assets/anim/' + FILES[state]);
    if (generation !== this.generation) return;
    if (LOOPING.has(state)) {
      this.renderer.start(true);
      return;
    }
    this.renderer.once(() => {
      if (generation !== this.generation) return;
      const next = AUTO[state];
      if (next) this.play(next);
    });
  }

  async playIdle(generation) {
    if (!this.idle || generation !== this.generation) return;
    const key = ['idle_start', 'idle_mid', 'idle_end'][this.idleIndex];
    await this.renderer.load('/assets/anim/' + FILES[key]);
    if (!this.idle || generation !== this.generation) return;
    this.renderer.once(() => {
      if (!this.idle || generation !== this.generation) return;
      this.idleIndex = (this.idleIndex + 1) % 3;
      this.playIdle(generation);
    });
  }

  nudge() {
    if (this.state !== AnimState.IDLE && this.state !== AnimState.STATIC) return;
    this.play(Math.random() < 0.5 ? AnimState.HOP : AnimState.BOW);
  }
}
